/**
 * ApprovalsTab — approval chain progress tab content.
 *
 * Extracted from DocumentModal.tsx to reduce render complexity.
 * Purely presentational: all state and handlers are passed as props.
 */

import React from 'react';
import { Loader2 } from 'lucide-react';
import { type DocumentFormState } from '../documentModalHelpers';
import { type Document, type DocumentType } from '@/hooks/useDocumentsWorkItems';
import { DocumentModalApprovalChain, type ApprovalChain } from '../../DocumentModalApprovalChain';

export interface ApprovalsTabProps {
  activeTab: 'items' | 'details' | 'notes' | 'attachments' | 'approvals';
  document: Document | null | undefined;
  isDarkMode: boolean;
  formData: DocumentFormState;
  doc_type: DocumentType;
  approvalChain: ApprovalChain | null;
  approvalChainLoading: boolean;
  onApprove: (stepId: string, notes?: string) => Promise<void>;
  onReject: (stepId: string, reason: string) => Promise<void>;
  onDelegate: (stepId: string, toUserId: number) => Promise<void>;
}

export function ApprovalsTab({
  activeTab,
  document,
  isDarkMode,
  formData,
  doc_type,
  approvalChain,
  approvalChainLoading,
  onApprove,
  onReject,
  onDelegate,
}: ApprovalsTabProps) {
  if (activeTab !== 'approvals' || !document?.id) return null;

  return (
    <div className={`p-6 ${isDarkMode ? 'bg-gray-800/50' : 'bg-gray-50'}`} data-tour-id="doc-modal-approval-chain">
      <div className="mb-6">
        <h3 className={`text-lg font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          Approval Chain
        </h3>
        <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          Track multi-level approval progress for this document.
        </p>
      </div>
      <DocumentModalApprovalChain
        document={{
          id: String(document.id),
          status: formData.status || document.status || 'draft',
          doc_type,
          doc_number: formData.doc_number || formData.item_number || document.item_number || undefined,
        }}
        approvalChain={approvalChain || undefined}
        onApprove={onApprove}
        onReject={onReject}
        onDelegate={onDelegate}
      />
      {approvalChainLoading && (
        <div className="mt-3 flex items-center gap-2 text-sm text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading approval chain...
        </div>
      )}
    </div>
  );
}

export default ApprovalsTab;
